import { z } from 'zod';
import {
  baseDocSchema,
  currencyCodeSchema,
  localeSchema,
  objectIdSchema,
  slugSchema,
} from './common.js';

export const eventStatusSchema = z.enum(['draft', 'live', 'ended', 'cancelled']);
export type EventStatus = z.infer<typeof eventStatusSchema>;

export const eventTranslationSchema = z.object({
  title: z.string().min(2).max(160),
  description: z.string().max(8000).default(''),
  venueLabel: z.string().max(160).default(''),
});
export type EventTranslation = z.infer<typeof eventTranslationSchema>;

export const eventSchema = baseDocSchema.extend({
  orgId: objectIdSchema,
  slug: slugSchema,
  bannerUrl: z.string().url().optional(),
  startsAt: z.coerce.date(),
  endsAt: z.coerce.date(),
  timezone: z.string().min(1).default('America/Mexico_City'),
  status: eventStatusSchema.default('draft'),
  defaultLocale: localeSchema.default('es'),
  supportedLocales: z.array(localeSchema).min(1).default(['es']),
  translations: z.record(localeSchema, eventTranslationSchema),
  currency: currencyCodeSchema.default('USD'),
  serviceFeePct: z.number().min(0).max(50).default(0),
  refundPolicy: z.enum(['none', 'until_24h', 'flexible']).default('none'),
  publishedAt: z.coerce.date().nullable().default(null),
  /**
   * EventTicketClass ID en Google Wallet (`<issuerId>.event-<eventId>`).
   * `null` si Wallet está deshabilitado o todavía no se emitió ningún
   * ticket con la integración activa.
   */
  googleWalletClassId: z.string().nullable().default(null),
});
export type Event = z.infer<typeof eventSchema>;

export const createEventInputSchema = z.object({
  slug: slugSchema,
  startsAt: z.coerce.date(),
  endsAt: z.coerce.date(),
  timezone: z.string().min(1),
  defaultLocale: localeSchema,
  supportedLocales: z.array(localeSchema).min(1),
  translations: z.record(localeSchema, eventTranslationSchema),
  // La moneda no se acepta del cliente: el server la deriva del país de la
  // org (default settlement currency de la cuenta Stripe Connect). Aceptamos
  // un valor opcional sólo para no romper clients existentes que lo manden;
  // el server lo ignora.
  currency: currencyCodeSchema.optional(),
  serviceFeePct: z.number().min(0).max(50).default(0),
  bannerUrl: z.string().url().optional(),
});
export type CreateEventInput = z.infer<typeof createEventInputSchema>;
